<script setup lang="ts">
import { computed } from 'vue';

import type { ModuleRecord } from '@/types/domain';
import { issueLevelLabel, recordLevel } from '@/utils/issue-escalation';

const props = defineProps<{ items: ModuleRecord[] }>();

const emit = defineEmits<{
  /**
   * 点某一格 → 把列表筛到那一类，图表不能只是好看。
   *
   * `kpi` 是 0916 加的：顶上那排数字原来只能看，而清单上方另有一排一模一样的
   * 磁贴负责筛选（全部 / 待处理 / 已逾期 / 未指定责任人）——同一组数字摆两遍。
   * 现在这一排既是读数也是入口，那排磁贴撤掉。
   */
  drill: [filter: {
    kind: 'status' | 'grade' | 'category' | 'owner' | 'aging' | 'level' | 'kpi';
    value: string;
  }];
}>();

const TERMINAL = new Set(['closed', 'completed', 'approved', 'cancelled', '已关闭']);
const today = new Date().toISOString().slice(0, 10);

function normalized(item: ModuleRecord): Record<string, unknown> {
  return item.normalized && typeof item.normalized === 'object'
    ? item.normalized as Record<string, unknown>
    : {};
}

function field(item: ModuleRecord, key: string): string {
  const value = item[key] ?? normalized(item)[key];
  return value === null || value === undefined || value === '' ? '' : String(value);
}

function isOpen(item: ModuleRecord): boolean {
  return !TERMINAL.has(String(item.status ?? ''));
}

function planDate(item: ModuleRecord): string {
  return (field(item, 'currentPlanDate') || String(item.due_at ?? item.dueAt ?? '')).slice(0, 10);
}

/**
 * 问题天数：从发现日期算到完成日期，未完成的算到今天。
 * 旧 EQS 列表里有这一列，是判断「这个问题拖了多久」最直接的指标。
 */
function ageDays(item: ModuleRecord): number | null {
  const found = (field(item, 'foundAt') || String(item.created_at ?? '')).slice(0, 10);
  if (!found) return null;
  const end = field(item, 'completedAt').slice(0, 10) || today;
  const days = Math.floor((Date.parse(end) - Date.parse(found)) / 86_400_000);
  return Number.isFinite(days) ? Math.max(0, days) : null;
}

function isOverdue(item: ModuleRecord): boolean {
  const plan = planDate(item);
  return Boolean(plan && isOpen(item) && plan < today);
}

interface Slice {
  label: string;
  value: string;
  count: number;
  tone?: 'risk' | 'warning' | 'normal';
}

function tally(
  key: (item: ModuleRecord) => string,
  fallback: string,
  toneOf?: (label: string) => Slice['tone'],
): Slice[] {
  const map = new Map<string, number>();
  for (const item of props.items) {
    const label = key(item) || fallback;
    map.set(label, (map.get(label) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([label, count]) => ({ label, value: label, count, tone: toneOf?.(label) }))
    .sort((a, b) => b.count - a.count);
}

/*
  「问题总数」不算已取消的（试用反馈 0819：取消 12 条后总数纹丝不动，
  看板上 24 个「问题」里一半其实已经不存在了）。
  取消 = 这条记录作废（提错了/重复了），它不是被解决的问题，也不是待解决的问题——
  两头都不算才对。但不能无声消失：卡片下方注明有几条已取消，点状态图仍能筛出来。
*/
const cancelledCount = computed(() =>
  props.items.filter((item) => String(item.status ?? '') === 'cancelled').length);
const total = computed(() => props.items.length - cancelledCount.value);
const openCount = computed(() => props.items.filter(isOpen).length);
const overdueCount = computed(() => props.items.filter(isOverdue).length);
/** 升到部门负责人 / 管理层的未关问题（0911 起层级取代旧的「需升级」开关） */
const escalated = computed(() => props.items.filter((item) => isOpen(item) && recordLevel(item) >= 3).length);
const unassigned = computed(() => props.items.filter((item) =>
  !(item.owner_name || item.ownerName || field(item, 'ownerName'))).length);
/*
  没排完成日期的未关闭问题（0916）。原来只有模块中心顶上那块通用看板有这一格，
  而那块看板整个和这一块重复，撤掉之前先把它独有的这一项挪过来，别少掉东西。

  为什么它重要：没有完成日期的问题永远不会「逾期」，催办和升级判据都够不着它——
  47 条未关闭里 47 条没日期，等于这些判据全程空转。
*/
const noPlanDate = computed(() => props.items.filter(
  (item) => isOpen(item) && !planDate(item),
).length);

const byStatus = computed(() => tally(
  (item) => String(item.status ?? ''),
  '未填状态',
  (label) => (TERMINAL.has(label) ? 'normal' : 'warning'),
));

/**
 * 只按「问题等级」(A-严重/B-重要/C-一般/D-轻微) 统计。
 *
 * 刻意不回落到 priority：那是另一套刻度（normal/high/高/中/低），
 * 混进来会出现「B-重要 33 / normal 5 / 高 1」这种一张图两把尺子的读数。
 * 没定级就老老实实叫「未定级」——那本身就是要补的数据缺口。
 */
const byGrade = computed(() => tally(
  (item) => field(item, 'severityGrade'),
  '未定级',
  (label) => (/^A/u.test(label) ? 'risk' : /^B/u.test(label) ? 'warning' : undefined),
));

const byCategory = computed(() => tally((item) => field(item, 'category'), '未分类'));

/** 未关闭问题按升级层级：谁在扛。关闭的不计——它们不再压在任何人头上 */
const byLevel = computed<Slice[]>(() => {
  const counts = new Map<number, number>();
  for (const item of props.items) {
    if (!isOpen(item)) continue;
    const level = recordLevel(item);
    counts.set(level, (counts.get(level) ?? 0) + 1);
  }
  return [4, 3, 2, 1].map((level) => ({
    label: issueLevelLabel(level), value: String(level), count: counts.get(level) ?? 0,
    tone: level >= 4 ? 'risk' as const : level === 3 ? 'warning' as const : undefined,
  }));
});

const byOwner = computed(() => tally(
  (item) => String(item.owner_name ?? item.ownerName ?? field(item, 'ownerName')),
  '未指定',
).slice(0, 8));

/**
 * 老化分桶：只统计未关闭的问题。
 * 已关闭的问题拖了多久是复盘用的，摆在「现在有多少压着」的看板里会稀释信号。
 */
const byAging = computed<Slice[]>(() => {
  const buckets: Array<{ label: string; value: string; max: number; tone?: Slice['tone'] }> = [
    { label: '7 天内', value: '0-7', max: 7 },
    { label: '8–30 天', value: '8-30', max: 30 },
    { label: '31–90 天', value: '31-90', max: 90, tone: 'warning' },
    { label: '90 天以上', value: '90+', max: Number.POSITIVE_INFINITY, tone: 'risk' },
  ];
  const counts = buckets.map((bucket) => ({ ...bucket, count: 0 }));
  for (const item of props.items) {
    if (!isOpen(item)) continue;
    const age = ageDays(item);
    if (age === null) continue;
    const hit = counts.find((bucket) => age <= bucket.max);
    if (hit) hit.count += 1;
  }
  return counts.map(({ label, value, count, tone }) => ({ label, value, count, tone }));
});

const maxOf = (slices: Slice[]): number => Math.max(1, ...slices.map((s) => s.count));

const charts = computed(() => [
  { key: 'status' as const, title: '按状态', slices: byStatus.value },
  { key: 'grade' as const, title: '按问题等级', slices: byGrade.value },
  { key: 'level' as const, title: '按升级层级（未关闭）', slices: byLevel.value },
  { key: 'category' as const, title: '按问题类型', slices: byCategory.value },
  { key: 'owner' as const, title: '按责任人（前 8）', slices: byOwner.value },
]);
</script>

<template>
  <section class="issue-dashboard" aria-label="问题分布看板">
    <!-- 这一排既是读数也是筛选入口（0916）：点哪个数字，下面的清单就筛到那一类 -->
    <div class="issue-kpis">
      <button type="button" @click="emit('drill', { kind: 'kpi', value: 'all' })">
        <b>{{ total }}</b><span>问题总数</span>
        <small v-if="cancelledCount">另有 {{ cancelledCount }} 条已取消，不计入</small>
      </button>
      <button type="button" @click="emit('drill', { kind: 'kpi', value: 'open' })">
        <b>{{ openCount }}</b><span>未关闭</span>
      </button>
      <button
        type="button" :class="{ risk: overdueCount > 0 }"
        @click="emit('drill', { kind: 'kpi', value: 'overdue' })"
      ><b>{{ overdueCount }}</b><span>已逾期</span></button>
      <button
        type="button" :class="{ warning: escalated > 0 }"
        @click="emit('drill', { kind: 'kpi', value: 'escalated' })"
      ><b>{{ escalated }}</b><span>已升到部门 / 管理层</span></button>
      <button
        type="button" :class="{ warning: unassigned > 0 }"
        @click="emit('drill', { kind: 'kpi', value: 'unassigned' })"
      ><b>{{ unassigned }}</b><span>未指定责任人</span></button>
      <button
        type="button" :class="{ warning: noPlanDate > 0 }"
        @click="emit('drill', { kind: 'kpi', value: 'noplan' })"
      >
        <b>{{ noPlanDate }}</b><span>未设完成日期</span>
        <small v-if="noPlanDate">没有日期就永远不会逾期，催办和升级都够不着</small>
      </button>
    </div>

    <div class="issue-charts">
      <article v-for="chart in charts" :key="chart.key">
        <h4>{{ chart.title }}</h4>
        <p v-if="!chart.slices.length" class="chart-empty">暂无数据</p>
        <ul v-else>
          <li v-for="slice in chart.slices" :key="slice.value">
            <button type="button" @click="emit('drill', { kind: chart.key, value: slice.value })">
              <span class="bar-label">{{ slice.label }}</span>
              <span class="bar-track">
                <i
                  :class="slice.tone"
                  :style="{ width: `${Math.round((slice.count / maxOf(chart.slices)) * 100)}%` }"
                ></i>
              </span>
              <b>{{ slice.count }}</b>
            </button>
          </li>
        </ul>
      </article>

      <article>
        <h4>未关闭问题的老化</h4>
        <p class="chart-note">从问题发现日算起。已关闭的不计入——那是复盘数据，会稀释「现在压着多少」。</p>
        <ul>
          <li v-for="slice in byAging" :key="slice.value">
            <button type="button" @click="emit('drill', { kind: 'aging', value: slice.value })">
              <span class="bar-label">{{ slice.label }}</span>
              <span class="bar-track">
                <i :class="slice.tone" :style="{ width: `${Math.round((slice.count / maxOf(byAging)) * 100)}%` }"></i>
              </span>
              <b>{{ slice.count }}</b>
            </button>
          </li>
        </ul>
      </article>
    </div>
  </section>
</template>

<style scoped>
.issue-dashboard { display: grid; gap: 14px; margin-bottom: 16px; }

.issue-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
  gap: 10px;
}

.issue-kpis button {
  display: grid;
  gap: 2px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: var(--white);
}

.issue-kpis button:hover { border-color: var(--blue); }
.issue-kpis button.risk { border-left-color: var(--risk); }
.issue-kpis button.warning { border-left-color: var(--warning-graphic); }
.issue-kpis b { font: 700 26px var(--display); }
.issue-kpis button.risk b { color: var(--risk); }
.issue-kpis button.warning b { color: var(--warning); }
.issue-kpis span { color: var(--muted); font-size: 12px; }
.issue-kpis small { color: var(--muted); font-size: 11px; line-height: 1.5; }

.issue-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}

.issue-charts article {
  padding: 12px 14px;
  border: 1px solid var(--line);
  background: var(--white);
}

.issue-charts h4 { margin: 0 0 8px; font-size: 13px; }
.chart-empty, .chart-note { margin: 0 0 8px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.issue-charts ul { display: grid; gap: 5px; margin: 0; padding: 0; list-style: none; }

.issue-charts button {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(64px, 96px) 1fr 34px;
  gap: 8px;
  align-items: center;
  padding: 3px 4px;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.issue-charts button:hover,
.issue-charts button:focus-visible { background: #EEF4FA; }

.bar-label {
  overflow: hidden;
  color: var(--ink);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-track { height: 14px; background: var(--paper); }
.bar-track i { display: block; height: 100%; min-width: 2px; background: var(--blue); }
.bar-track i.risk { background: var(--risk); }
.bar-track i.warning { background: var(--warning-graphic); }
.bar-track i.normal { background: var(--green); }
.issue-charts button b { font-size: 12px; text-align: right; }
.issue-kpis article small { display: block; margin-top: 2px; color: var(--muted); font-size: 11px; }
</style>
